import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProjectDetail } from '@/hooks/useProjectDetail';
import { useProjectMembers } from '@/hooks/useProjectMembers';
import { usePeopleSearch, type PersonResult } from '@/hooks/usePeopleSearch';
import { projectKeys } from '@/hooks/useProjectsQuery';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, X, Link2, Search, Loader2, User } from 'lucide-react';
import SharePointFolderModal from '../SharePointFolderModal';
import { BACKEND_URL } from '@/lib/api';

const API_BASE_URL = `${BACKEND_URL}/api/v1`;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ProjectCreationViewProps {
  onBack?: () => void;
  projectId?: string;
}

interface ProjectMember {
  id: string;
  email: string;
  display_name: string;
}

export default function ProjectCreationView({ onBack, projectId: editProjectId }: ProjectCreationViewProps) {
  const { backendToken } = useAuth();
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const urlProjectId = search?.projectId || editProjectId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [projectTitle, setProjectTitle] = useState('');
  const [sharePointFolders, setSharePointFolders] = useState<Array<{ folderPath: string; folderId: string }>>([]);
  const [showSharePointModal, setShowSharePointModal] = useState(false);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [tempProjectId] = useState(`temp-${Date.now()}`);
  const [submitting, setSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Inline member search
  const [showMemberSearch, setShowMemberSearch] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const excludeEmails = useMemo(() => members.map(m => m.email), [members]);
  const { results: memberResults, searching: searchingMembers } = usePeopleSearch(memberQuery, excludeEmails);

  // Load project data if editing (uses cached query hook)
  const { data: editProject } = useProjectDetail(urlProjectId || '');
  const { data: existingMembers } = useProjectMembers(urlProjectId || '');
  const seededRef = useRef(false);

  useEffect(() => {
    if (!urlProjectId || seededRef.current) return;
    if (!editProject || existingMembers === undefined) return;
    seededRef.current = true;

    const project = editProject as Record<string, unknown>;
    setProjectTitle((project.name as string) || '');
    const paths = project.folder_paths as string[] | null;
    if (paths && paths.length > 0) {
      setSharePointFolders(paths.map(p => ({ folderPath: p, folderId: '' })));
    }

    if (Array.isArray(existingMembers) && existingMembers.length > 0) {
      setMembers(
        (existingMembers as Array<{ user_id?: string; id: string; email: string; display_name: string }>)
          .map(m => ({
            id: m.user_id || m.id,
            email: m.email,
            display_name: m.display_name,
          }))
      );
    }
  }, [editProject, existingMembers, urlProjectId]);

  // Focus search input when opening
  useEffect(() => {
    if (showMemberSearch) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    } else {
      setMemberQuery('');
    }
  }, [showMemberSearch]);

  const handleAddPerson = (person: PersonResult) => {
    if (!person.email) return;
    setMembers(prev => [
      ...prev,
      { id: person.id, email: person.email!, display_name: person.displayName },
    ]);
    setMemberQuery('');
  };

  const handleRemoveMember = (memberId: string) => {
    setMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const handleSharePointSelect = (_projectId: string, folderPath: string, folderId: string) => {
    setSharePointFolders(prev => {
      if (prev.some(f => f.folderPath === folderPath)) return prev;
      return [...prev, { folderPath, folderId }];
    });
    setShowSharePointModal(false);
  };

  const handleSubmit = async () => {
    if (!projectTitle.trim()) return;
    setFormMessage(null);

    const token = backendToken;
    if (!token) {
      setFormMessage({ type: 'error', text: 'Authentication required. Please sign in again.' });
      return;
    }

    setSubmitting(true);
    try {
      const isEditing = !!urlProjectId;
      const formData = new FormData();
      formData.append('name', projectTitle);

      const validMemberIds = members.map(m => m.id).filter(id => UUID_RE.test(id));
      if (isEditing || validMemberIds.length > 0) {
        formData.append('team_members', JSON.stringify(validMemberIds));
      }

      if (isEditing) {
        formData.append(
          'sharepoint_path',
          sharePointFolders.length > 0
            ? JSON.stringify(sharePointFolders.map(f => f.folderPath))
            : '',
        );
      } else if (sharePointFolders.length > 0) {
        formData.append('sharepoint_path', JSON.stringify(sharePointFolders.map(f => f.folderPath)));
      }

      const url = isEditing
        ? `${API_BASE_URL}/projects/${urlProjectId}`
        : `${API_BASE_URL}/projects`;
      const method = isEditing ? 'PATCH' : 'POST';

      const createResponse = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => null);
        console.error("Project save failed:", createResponse.status, errorData);
        throw new Error(isEditing
          ? 'Failed to save project. Please try again.'
          : 'Failed to create project. Please try again.');
      }

      if (isEditing) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: projectKeys.all }),
          queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/projects/{project_id}", { params: { path: { project_id: urlProjectId } } }] }),
          queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/projects/{project_id}/members", { params: { path: { project_id: urlProjectId } } }] }),
        ]);
        if (onBack) {
          onBack();
        } else {
          navigate({ to: "/dashboard" });
        }
      } else {
        const responseData = await createResponse.json();
        const projectResponse = responseData.project || responseData;
        const projectId = projectResponse.id || projectResponse.project_id;

        if (!projectId) {
          throw new Error('Project created but no ID returned.');
        }

        await queryClient.invalidateQueries({ queryKey: projectKeys.all });
        if (onBack) {
          onBack();
        } else {
          navigate({ to: "/dashboard" });
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save project.';
      setFormMessage({ type: 'error', text: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
        <h1 className="text-xl font-semibold text-foreground">{urlProjectId ? 'Edit Project' : 'Create Project'}</h1>
        {onBack && (
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted/50 text-foreground transition-colors"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-6 space-y-6">

          {/* Project Name */}
          <div className="bg-card border border-border rounded-xl p-5">
            <label className="block text-sm font-semibold text-foreground mb-2">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              className="w-full px-3.5 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              placeholder="Enter project name"
            />
          </div>

          {/* Team Members */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground">
                Team Members
                {members.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    ({members.length})
                  </span>
                )}
              </h2>
              <button
                onClick={() => setShowMemberSearch(!showMemberSearch)}
                className="px-3 py-1.5 bg-foreground text-background text-xs font-medium rounded-lg hover:bg-foreground/90 transition-colors flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Member
              </button>
            </div>

            {/* Inline search */}
            {showMemberSearch && (
              <div className="mb-4 border border-border rounded-lg overflow-hidden">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={memberQuery}
                    onChange={(e) => setMemberQuery(e.target.value)}
                    placeholder="Search by name..."
                    className="w-full pl-10 pr-10 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground border-b border-border focus:outline-none"
                  />
                  {searchingMembers && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                  )}
                  {!searchingMembers && memberQuery && (
                    <button
                      onClick={() => setMemberQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="max-h-48 overflow-y-auto bg-background">
                  {memberQuery.length < 2 ? (
                    <div className="px-4 py-6 text-xs text-muted-foreground text-center">
                      Type at least 2 characters to search
                    </div>
                  ) : searchingMembers ? (
                    <div className="px-4 py-6 text-xs text-muted-foreground text-center">
                      Searching...
                    </div>
                  ) : memberResults.length > 0 ? (
                    <div className="divide-y divide-border">
                      {memberResults.map((person) => (
                        <button
                          key={person.id}
                          type="button"
                          onClick={() => handleAddPerson(person)}
                          className="w-full text-left px-4 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-3"
                        >
                          <div className="w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center shrink-0">
                            {(person.displayName || 'U')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">
                              {person.displayName}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {person.email}
                              {person.jobTitle && ` -- ${person.jobTitle}`}
                            </div>
                          </div>
                          <Plus className="w-4 h-4 text-muted-foreground shrink-0" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-xs text-muted-foreground text-center">
                      No people found matching &ldquo;{memberQuery}&rdquo;
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Member grid (TeamTab style) */}
            {members.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="bg-background rounded-lg border border-border p-3 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary text-white text-sm font-medium flex items-center justify-center shrink-0">
                        {(member.display_name || member.email || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {member.display_name || member.email}
                        </div>
                        {member.display_name && (
                          <div className="text-xs text-muted-foreground truncate">{member.email}</div>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="p-1 hover:bg-red-50 rounded text-muted-foreground hover:text-red-600 transition-colors shrink-0"
                        title="Remove member"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : !showMemberSearch ? (
              <div className="text-center py-8 border border-dashed border-border rounded-lg">
                <User className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No team members added yet</p>
              </div>
            ) : null}
          </div>

          {/* Data Sources */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="text-sm font-semibold text-foreground mb-3">Data Sources</h2>

            {sharePointFolders.length > 0 && (
              <div className="space-y-2 mb-3">
                {sharePointFolders.map((folder) => {
                  const isOneDrive = folder.folderPath.toLowerCase().includes('-my.sharepoint.com');
                  return (
                    <div
                      key={folder.folderPath}
                      className="flex items-center gap-2 p-2.5 bg-muted/50 rounded-lg"
                    >
                      <Link2 className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span
                        className="text-xs px-1.5 py-0.5 rounded font-medium shrink-0"
                        style={{
                          backgroundColor: isOneDrive ? '#EFF6FF' : '#F0FDF4',
                          color: isOneDrive ? '#2563EB' : '#16A34A',
                        }}
                      >
                        {isOneDrive ? 'OD' : 'SP'}
                      </span>
                      <span className="text-sm text-foreground truncate flex-1">{folder.folderPath}</span>
                      <button
                        onClick={() => setSharePointFolders(prev => prev.filter(f => f.folderPath !== folder.folderPath))}
                        className="text-muted-foreground hover:text-red-600 shrink-0 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setShowSharePointModal(true)}
              className="w-full px-3.5 py-2.5 text-sm border border-dashed border-border rounded-lg flex items-center justify-center gap-2 hover:bg-muted/30 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Link2 className="w-4 h-4" />
              {sharePointFolders.length > 0 ? 'Add Another Data Source' : 'Connect Data Source'}
            </button>
          </div>

          {/* Inline feedback */}
          {formMessage && (
            <div className={`px-4 py-3 rounded-lg text-sm ${
              formMessage.type === 'error'
                ? 'bg-destructive/5 border border-destructive/20 text-destructive'
                : 'bg-green-50 border border-green-200 text-green-700'
            }`}>
              {formMessage.text}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!projectTitle.trim() || submitting}
            className="w-full px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting
              ? (urlProjectId ? 'Saving...' : 'Creating...')
              : (urlProjectId ? 'Save Changes' : 'Create Project')}
          </button>
        </div>
      </div>

      {/* SharePoint Folder Modal */}
      <SharePointFolderModal
        projectId={tempProjectId}
        isOpen={showSharePointModal}
        onClose={() => setShowSharePointModal(false)}
        onSelectFolder={handleSharePointSelect}
      />
    </div>
  );
}
